import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Card, Divider, Grid, Typography } from '@mui/material';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';

// project imports
import config from 'config';
import { gridSpacing } from 'metadata/constant';

// assets
import { IconTallymark1 } from '@tabler/icons-react';
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone';
import HomeIcon from '@mui/icons-material/Home';
import HomeTwoToneIcon from '@mui/icons-material/HomeTwoTone';

const linkSX = {
    display: 'flex',
    color: 'grey.900',
    textDecoration: 'none',
    alignContent: 'center',
    alignItems: 'center'
};

// ==============================|| BREADCRUMBS ||============================== //

const Breadcrumbs = ({ card, divider, icon, icons, maxItems, navigation, rightAlign, separator, title, titleBottom, ...others }) => {
    const theme = useTheme();

    const iconStyle = {
        marginRight: theme.spacing(0.75),
        marginTop: `-${theme.spacing(0.25)}`,
        width: '1rem',
        height: '1rem',
        color: theme.palette.primary.main
    };

    const [items, setItems] = useState([]);

    const isMatch = (pattern, path) => {
        if (pattern.includes(':id')) {
            const regex = new RegExp('^' + pattern.replace(':id', '[^/]+') + '$');
            return regex.test(path);
        } else {
            return path === pattern || path.startsWith(pattern + '/');
        }
    };

    const resolveBreadcrumbs = (menu, items, path = []) => {
        // Create a new path array that includes the current menu
        const currentPath = [...path];
        if (menu.url) {
            currentPath.push(menu);

            const urlPattern = new URLPattern({ pathname: `${menu.url}*` });
            const result = urlPattern.exec(`${document.location.href}`);

            if (result && isMatch(menu.url, document.location.pathname)) {
                // If this is an exact match for the current URL, add the entire path to items
                if (document.location.pathname === config.basename + menu.url) {
                    items.push(...currentPath);
                    return true;
                }

                // If this is a parent of the current URL, continue searching but mark it as a potential match
                if (menu.children) {
                    const foundInChildren = menu.children.some((item) => resolveBreadcrumbs(item, items, currentPath));
                    if (foundInChildren) {
                        return true;
                    }
                }

                // If no match found in children but this is still a match for the URL pattern,
                // add the current path to items
                items.push(...currentPath);
                return true;
            }
        }

        // If this menu doesn't match but has children, check them
        if (menu.children) {
            const foundInChildren = menu.children.some((item) => resolveBreadcrumbs(item, items, currentPath));
            return foundInChildren;
        }

        return false;
    };

    useEffect(() => {
        const items = [];
        const currentPath = window.location.pathname;

        navigation?.items?.map((menu) => {
            if (menu.type && menu.type === 'group') {
                resolveBreadcrumbs(menu, items);
            }
            return false;
        });

        // Remove duplicates and sort by URL path length to ensure correct order
        const uniqueItems = [];
        const itemIds = new Set();

        items.forEach((item) => {
            if (!itemIds.has(item.id)) {
                itemIds.add(item.id);
                uniqueItems.push(item);
            }
        });

        // Sort by URL path length (shorter paths first)
        uniqueItems.sort((a, b) => {
            if (!a.url) return -1;
            if (!b.url) return 1;
            return a.url.split('/').length - b.url.split('/').length;
        });

        setItems(uniqueItems);
    }, [navigation]);

    // item separator
    const SeparatorIcon = separator;
    const separatorIcon = separator ? <SeparatorIcon stroke={1.5} size="1rem" /> : <IconTallymark1 stroke={1.5} size="1rem" />;

    let mainContent;
    let breadcrumbContent = <Typography />;
    let itemTitle = '';
    let ItemIcon;

    // items
    if (items && items.length > 0) {
        const itemContent = items.map((item) => {
            itemTitle = item.title;
            ItemIcon = item.icon ? item.icon : AccountTreeTwoToneIcon;
            return (
                <Typography
                    variant="subtitle1"
                    sx={{
                        display: 'flex',
                        textDecoration: 'none',
                        alignContent: 'center',
                        alignItems: 'center',
                        color: 'grey.500'
                    }}
                    key={item.id}
                >
                    {icons && <ItemIcon style={iconStyle} />}
                    {itemTitle}
                </Typography>
            );
        });

        // main
        breadcrumbContent = (
            <Card
                sx={{
                    marginBottom: card === false ? 0 : theme.spacing(gridSpacing),
                    border: card === false ? 'none' : '1px solid',
                    borderColor: theme.palette.primary[200] + 75,
                    background: card === false ? 'transparent' : theme.palette.background.default
                }}
                {...others}
            >
                <Box sx={{ p: 2, pl: card === false ? 0 : 2 }}>
                    <Grid
                        container
                        direction={rightAlign ? 'row' : 'column'}
                        justifyContent={rightAlign ? 'space-between' : 'flex-start'}
                        alignItems={rightAlign ? 'center' : 'flex-start'}
                        spacing={1}
                    >
                        {title && !titleBottom && (
                            <Grid item>
                                <Typography variant="h3" sx={{ fontWeight: 500 }}>
                                    {items[items.length - 1].title}
                                </Typography>
                            </Grid>
                        )}
                        <Grid item>
                            <MuiBreadcrumbs
                                sx={{ '& .MuiBreadcrumbs-separator': { width: 16, ml: 1.25, mr: 1.25 } }}
                                aria-label="breadcrumb"
                                maxItems={maxItems || 8}
                                separator={separatorIcon}
                            >
                                <Typography component={Link} to="/" color="inherit" variant="subtitle1" sx={linkSX} key="home">
                                    {icons && <HomeTwoToneIcon sx={iconStyle} />}
                                    {icon && <HomeIcon sx={{ ...iconStyle, mr: 0 }} />}
                                    {!icon && 'Dashboard'}
                                </Typography>
                                {itemContent}
                            </MuiBreadcrumbs>
                        </Grid>
                        {title && titleBottom && (
                            <Grid item>
                                <Typography variant="h3" sx={{ fontWeight: 500 }}>
                                    {items[items.length - 1].title}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Box>
                {card === false && divider !== false && <Divider sx={{ borderColor: theme.palette.primary.main, mb: gridSpacing }} />}
            </Card>
        );
    }

    return breadcrumbContent;
};

Breadcrumbs.propTypes = {
    card: PropTypes.bool,
    divider: PropTypes.bool,
    icon: PropTypes.bool,
    icons: PropTypes.bool,
    maxItems: PropTypes.number,
    navigation: PropTypes.object,
    rightAlign: PropTypes.bool,
    separator: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
    title: PropTypes.bool,
    titleBottom: PropTypes.bool
};

export default Breadcrumbs;
